import React, { useState } from "react";
import { api, formatError } from "../lib/api.js";

const CONTACT_OUTCOMES = [
  { value: "Contactado", label: "Contactado — cliente confirmou" },
  { value: "Sem resposta", label: "Sem resposta" },
  { value: "Pediu mais tempo", label: "Cliente pediu mais tempo" },
];

const QUOTE_RESPONSE_OUTCOMES = [
  { value: "Aprovado", label: "Aprovado — cliente aceitou" },
  { value: "Recusou", label: "Recusou — cliente não aceitou" },
  { value: "Pediu revisão", label: "Pediu revisão do orçamento" },
  { value: "Sem resposta", label: "Sem resposta" },
];

const DELIVERY_SCHEDULE_OUTCOMES = [
  { value: "Entrega marcada", label: "Entrega marcada com o cliente" },
  { value: "Pediu mais tempo", label: "Cliente pediu mais tempo" },
  { value: "Sem resposta", label: "Sem resposta" },
];

const COURTESY_OUTCOMES = [
  { value: "Cliente satisfeito", label: "Cliente satisfeito" },
  { value: "Cliente com reclamação", label: "Cliente com reclamação" },
  { value: "Sem resposta", label: "Sem resposta" },
];

const ETA_OUTCOMES = [
  { value: "Cliente informado", label: "Cliente informado sobre ETA" },
  { value: "Sem resposta", label: "Sem resposta" },
];

// Trigger-key driven outcome menus. Add new entries here as the auto-task
// catalogue grows in workflow.py.
const OUTCOMES_BY_TRIGGER = {
  pec_contact_customer_parts_received: CONTACT_OUTCOMES,
  pec_inform_customer_eta: ETA_OUTCOMES,
  som_send_quote_to_customer: QUOTE_RESPONSE_OUTCOMES,
  som_followup_approval: QUOTE_RESPONSE_OUTCOMES,
  som_schedule_delivery: DELIVERY_SCHEDULE_OUTCOMES,
  som_courtesy_call: COURTESY_OUTCOMES,
};

// Human-readable label for the outcome select per trigger.
const OUTCOME_LABEL_BY_TRIGGER = {
  pec_contact_customer_parts_received: "Resultado do contacto *",
  pec_inform_customer_eta: "Resultado do contacto *",
  som_send_quote_to_customer: "Resposta do cliente *",
  som_followup_approval: "Resposta do cliente *",
  som_schedule_delivery: "Marcação da entrega *",
  som_courtesy_call: "Resultado da chamada *",
};

/**
 * Shared dialog for completing or reopening a task.
 * Mode:
 *   - "complete" → asks for completion notes (+ optional structured outcome),
 *     sets status to "Concluída"
 *   - "reopen"   → asks for reason, sets status to "Reaberta"
 */
export default function CompleteTaskDialog({ task, mode = "complete", onClose, onDone }) {
  const isReopen = mode === "reopen";
  const [text, setText] = useState("");
  const outcomes = !isReopen && task?.trigger_key ? OUTCOMES_BY_TRIGGER[task.trigger_key] : null;
  const [outcome, setOutcome] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError("");
    if (!text.trim()) {
      setError(isReopen ? "Indique o motivo da reabertura." : "Indique as notas de conclusão.");
      return;
    }
    if (outcomes && !outcome) {
      setError("Indique o resultado do contacto.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = isReopen
        ? { status: "Reaberta", reopen_reason: text, comment: text }
        : {
            status: "Concluída",
            completion_notes: text,
            comment: text,
            ...(outcome ? { outcome } : {}),
          };
      await api.patch(`/tasks/${task.id}`, payload);
      onDone();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const title = isReopen ? "Reabrir tarefa" : "Concluir tarefa";
  const label = isReopen ? "Motivo da reabertura *" : "Notas de conclusão *";
  const submitLabel = isReopen ? "Reabrir" : "Concluir";
  const testid = isReopen ? "reopen-task-dialog" : "complete-task-dialog";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="px-5 py-3 border-b border-gray-200">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <div className="text-xs text-[#6C757D] mt-1">{task.title}</div>
        </div>
        <div className="p-5 space-y-3">
          {outcomes && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{OUTCOME_LABEL_BY_TRIGGER[task.trigger_key] || "Resultado *"}</span>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm bg-white"
                data-testid="completion-outcome"
              >
                <option value="">Selecionar…</option>
                {outcomes.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className="block text-[11px] text-[#6C757D] mt-1">
                A escolha regista automaticamente o contacto na obra e, quando aplicável, avança o ciclo associado.
              </span>
            </label>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm h-20"
              data-testid={isReopen ? "reopen-reason" : "completion-notes"}
            />
          </label>
          {error && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] rounded-sm text-sm">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]" data-testid={isReopen ? "reopen-task-cancel" : "complete-task-cancel"}>
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className={`px-4 py-2 text-sm text-white rounded-sm disabled:opacity-60 ${isReopen ? "bg-[#B45309] hover:bg-[#92400E]" : "bg-[#1F3864] hover:bg-[#142542]"}`}
            data-testid={isReopen ? "reopen-task-submit" : "complete-task-submit"}
          >
            {submitting ? "A guardar…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
